import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import type { JSX } from 'react'
import type * as React from 'react'
import { useSearchParams } from 'react-router-dom'

import {
  Button,
  CoachMark,
  Flex,
  NewBadge,
  useSidebar,
} from '@buffer-mono/popcorn'

import { useDismissableBanner } from '~publish/hooks/useDismissableBanner'

import styles from './SecondChannelCelebration.module.css'

export const SECOND_CHANNEL_TOUR_ID = 'second-channel-tour'

type TourStep = 0 | 1

type SecondChannelTourContextValue = {
  isActive: boolean
  targetChannelId: string | null
  step: TourStep
  next: () => void
  dismiss: () => void
}

const SecondChannelTourContext = createContext<SecondChannelTourContextValue>({
  isActive: false,
  targetChannelId: null,
  step: 0,
  next: () => {},
  dismiss: () => {},
})

function useSecondChannelTourContext(): SecondChannelTourContextValue {
  return useContext(SecondChannelTourContext)
}

export function SecondChannelTourProvider({
  channels,
  children,
}: {
  channels: { id: string }[]
  children: React.ReactNode
}): JSX.Element {
  const [step, setStep] = useState<TourStep>(0)
  const { isActive: bannerActive, dismiss: dismissBanner } =
    useDismissableBanner(SECOND_CHANNEL_TOUR_ID)
  const { state } = useSidebar()
  const [searchParams] = useSearchParams()
  const hasChannelConnectionSuccess =
    searchParams.get('channelConnectionSuccess') === 'true'

  const [isChannelConnectionModalOpen, setIsChannelConnectionModalOpen] =
    useState(
      () =>
        window.appshell?.orchestratorStore?.getState()?.modals?.modal != null,
    )

  useEffect(() => {
    const store = window.appshell?.orchestratorStore
    if (!store) return
    return store.subscribe(() => {
      setIsChannelConnectionModalOpen(store.getState()?.modals?.modal != null)
    })
  }, [])

  const isActive =
    bannerActive &&
    state === 'expanded' &&
    channels.length === 2 &&
    hasChannelConnectionSuccess &&
    !isChannelConnectionModalOpen
  const targetChannelId = isActive
    ? (channels[channels.length - 1]?.id ?? null)
    : null

  const next = useCallback(() => {
    setStep(1)
  }, [])

  useEffect(() => {
    if (!isActive) return

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        dismissBanner()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return (): void => document.removeEventListener('keydown', handleKeyDown)
  }, [isActive, dismissBanner])

  const contextValue = useMemo(
    () => ({
      isActive,
      targetChannelId,
      step,
      next,
      dismiss: dismissBanner,
    }),
    [isActive, targetChannelId, step, next, dismissBanner],
  )

  return (
    <SecondChannelTourContext.Provider value={contextValue}>
      {children}
    </SecondChannelTourContext.Provider>
  )
}

/**
 * Step 1: Wraps the channel item with a CoachMark.
 * Renders the "Your new channels' list" content when step === 0.
 */
export function SecondChannelTourStepOneCoachMark({
  channelId,
  children,
}: {
  channelId: string
  children: React.ReactNode
}): JSX.Element {
  const { isActive, targetChannelId, step, next, dismiss } =
    useSecondChannelTourContext()

  if (!isActive || step !== 0 || channelId !== targetChannelId) {
    return <>{children}</>
  }

  return (
    <CoachMark open dismissTimeout={null} onDismiss={dismiss}>
      {children}
      <CoachMark.Content
        side="right"
        align="center"
        showCloseIcon
        className={styles.content}
      >
        <CoachMark.Title>
          <Flex gap="xs" align="center">
            <NewBadge /> Your new channels&apos; list (1/2)
          </Flex>
        </CoachMark.Title>
        <CoachMark.Description>
          Expand each one to jump straight to Publish, Community, or Analytics
          for that channel.
        </CoachMark.Description>
        <CoachMark.Footer>
          <Button variant="primary" onClick={next}>
            Next
          </Button>
        </CoachMark.Footer>
      </CoachMark.Content>
    </CoachMark>
  )
}

/**
 * Step 1 overlay: Wraps the trigger element (e.g. ChannelItemSuffix)
 * with CoachMark.Overlay for arrow positioning.
 */
export function SecondChannelTourStepOneOverlay({
  channelId,
  children,
}: {
  channelId: string
  children: React.ReactNode
}): JSX.Element {
  const { isActive, targetChannelId, step } = useSecondChannelTourContext()

  if (!isActive || step !== 0 || channelId !== targetChannelId) {
    return <>{children}</>
  }

  return (
    <CoachMark.Overlay className={styles.overlay}>{children}</CoachMark.Overlay>
  )
}

/**
 * Step 2: Wraps the Publish nav button with a CoachMark.
 * Renders the "Overview at the top" content when step === 1.
 * Combines CoachMark root + Overlay since both wrap the same target.
 */
export function SecondChannelTourStepTwoCoachMark({
  children,
}: {
  children: React.ReactNode
}): JSX.Element {
  const { isActive, step, dismiss } = useSecondChannelTourContext()

  if (!isActive || step !== 1) {
    return <>{children}</>
  }

  return (
    <CoachMark open dismissTimeout={null} onDismiss={dismiss}>
      <CoachMark.Overlay className={styles.overlay}>
        {children}
      </CoachMark.Overlay>
      <CoachMark.Content
        side="right"
        align="center"
        showCloseIcon
        className={styles.content}
      >
        <CoachMark.Title>
          <Flex gap="xs" align="center">
            <NewBadge /> Overview at the top (2/2)
          </Flex>
        </CoachMark.Title>
        <CoachMark.Description>
          Find a view across all your channels here.
        </CoachMark.Description>
        <CoachMark.Footer>
          <Button variant="primary" onClick={dismiss}>
            Got It
          </Button>
        </CoachMark.Footer>
      </CoachMark.Content>
    </CoachMark>
  )
}
